import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { Check } from '@mui/icons-material';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomPagination from '../../uiParts/CustomPagination';
import CustomSelectField from '../../uiParts/CustomSelectField';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import SideBar from '../../uiParts/SideBar';
import MaterialCard, { ItemProps } from '../../uiParts/MaterialCard';
import { response } from '../../../api/db';
import { deleteFavorite, getFavorites } from '../../../api/db/favorite';
import {
  gray100,
  primaryButtonOutlinedColor,
  primaryColor
} from '../../../constants/Colors';
import { sideBarWidth } from '../../../constants/Location';
import { productMethodList } from '../../../constants/SelectOptions';
import { AuthContext } from '../../../contexts/auth';
import { common } from '../../../modules/error';
import { FavoriteGetProductType } from '../../../validation/favorite/favoriteType';
import CustomHead from '../CustomHead';
import {
  orderOnGetFavoriteProducts,
  productStatus
} from '../../../constants/Enum';
import { addMylist, deleteMylistProduct } from '../../../api/db/mylist';
import { sentryLog } from '../../../config/sentry';

const styles = {
  content: {
    pt: '96px',
    '@media screen and (max-width: 960px)': {
      p: '64px 16px 0'
    }
  },
  contentHeader: {
    alignItems: 'center',
    justifyContent: 'space-between',
    p: '32px 32px 24px',
    '@media screen and (max-width: 960px)': {
      p: '24px 16px'
    },
    '@media screen and (max-width: 600px)': {
      flexDirection: 'column'
    },

    // for select-field
    '& .MuiSelect-select': {
      p: '17px 12px 10px 16px'
    },
    '& .MuiTypography-root.MuiTypography-body1': {
      m: '0'
    },
    '& .MuiTypography-root.MuiTypography-body1 fieldset': {
      height: '48px',
      top: '-5.5px'
    },
    '& .MuiTypography-root.MuiTypography-body1 p': {
      color: primaryColor,
      fontWeight: '500'
    },
    '& .MuiTypography-root.MuiTypography-body1 svg': {
      top: 'calc(50% - 10.5px)'
    }
  },
  contentText: {
    color: gray100,
    mt: '24px',
    textAlign: 'center',
    '@media screen and (max-width: 960px)': {
      mt: '16px'
    }
  },
  contentTitle: {
    color: gray100,
    fontSize: '24px',
    fontWeight: '500',
    mt: '16px',
    textAlign: 'center',
    '@media screen and (max-width: 960px)': {
      textAlign: 'center'
    }
  },
  contentWrapper: {
    ml: sideBarWidth,
    width: '100%',
    '@media screen and (max-width: 960px)': {
      ml: '0'
    }
  },
  divider: {
    m: '0 32px',
    '@media screen and (max-width: 960px)': {
      m: '0 16px'
    }
  },
  headerTitle: {
    color: gray100,
    fontSize: '32px',
    fontWeight: '500',
    '@media screen and (max-width: 960px)': {
      fontSize: '24px',
      mb: '24px'
    }
  },
  headerLeft: {
    width: 'auto',
    '@media screen and (max-width: 600px)': {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'space-between'
    }
  },
  headerRight: {
    columnGap: '16px',
    flexDirection: 'row',
    width: 'auto',
    '@media screen and (max-width: 600px)': {
      mt: '20px',
      width: '100%',
      '&.MuiGrid-container div': {
        width: '100%'
      }
    }
  },
  icon: {
    fontSize: '80px',
    width: '100%',
    '@media screen and (max-width: 960px)': {
      fontSize: '64px'
    }
  },
  materialList: {
    p: '0 32px',
    '@media screen and (max-width: 960px)': {
      p: '0 16px'
    }
  },
  pageWrapper: {
    width: '100%',
    '@media screen and (max-width: 960px)': {
      flexDirection: 'column'
    }
  },
  searchButton: {
    mt: '80px',
    maxWidth: '343px',
    '@media screen and (max-width: 960px)': {
      mt: '48px'
    }
  }
};

const FavoritesList: React.FC = () => {
  const navigate = useNavigate();
  const { isLoggedIn, userId, userRank, userType } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [infoText, setInfoText] = useState('');
  const [showList, setShowList] = useState<boolean>(false);
  const [displayList, setDisplayList] = useState<ItemProps[]>([]);
  // お気に入り数
  const [totalCount, setTotalCount] = useState(0);

  // 計算方法
  const [selectedMethod, setSelectedMethod] = useState(
    Number(localStorage.getItem('selectedMethod')) || 1
  );
  const handleChangeMethod = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as number;
    setSelectedMethod(value);
    localStorage.setItem('selectedMethod', String(value));

    window.dataLayer.push({
      event: 'co2_method',
      screen: window.location.pathname,
      category: 'pulldown',
      action: 'favorite_co2_method',
      user_id: userId,
      user_type: userType,
      user_rank: userRank
    });
  };
  // ページネーション
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const handlePageChange = (event: ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    topRef?.current?.scrollIntoView();
  };
  const topRef = useRef<HTMLDivElement>(null);

  // ログインチェック
  useEffect((): void => {
    if (!isLoggedIn) {
      navigate('/auth/login');
      return;
    }
  }, []);

  const getList = async () => {
    const limit = 20;
    const offset = (currentPage - 1) * limit;
    const order = orderOnGetFavoriteProducts.descCreatedAt;

    // お気に入り取得
    const fetchedData = await getFavorites(
      limit,
      offset,
      order,
      selectedMethod
    );

    // お気に入り数を取得
    setTotalCount(fetchedData.total_count);

    // ページネーション
    const totalPages = Math.ceil(fetchedData.total_count / limit);
    setTotalPages(totalPages);

    // 表示用データに変換
    const displayList = fetchedData.products.map(
      (product: FavoriteGetProductType) => {
        // 日付の表示形式を変更
        const updateDate = new Date(product.updated_at);
        const month = updateDate.getMonth() + 1;
        const formattedMonth = month + 1 < 10 ? '0' + month : '' + month;
        const day = updateDate.getDate();
        const formattedDay = day < 10 ? '0' + day : '' + day;
        const formattedUpdatedDate = `${updateDate.getFullYear()}/${formattedMonth}/${formattedDay}`;

        const sortedImageData = product.image.sort(
          (a, b) => ((a && a.sort) || 0) - ((b && b.sort) || 0)
        );

        return {
          productId: product.id,
          productInfoId: product.product_info_id,
          alias: product.alias,
          category: product.category.name,
          material: product.material.name,
          name: product.name,
          specification: product.specification,
          summary: product.summary,
          emissionIntensity: product.emission_intensity,
          reduceRate: product.reduce_rate,
          calculationValue: product.calculated_emission_intensity,
          calculationBasisType: [product.calculation_basis_type],
          calculationBasisName: product.calculation_basis_name,
          company: product.users.company,
          myListNum: product.mylist_number,
          score: product.evaluation_score,
          recommendFlg: [product.recommend_flg],
          title: product.evaluations.title,
          content: product.evaluations.content,
          updateDate: formattedUpdatedDate,
          myListFlg: Boolean(product.mylist_flg),
          favoriteFlg: Boolean(product.favorite_flg),
          referenceUnitPrice: product.reference_unit_price,
          reductionAmount: product.calculated_reduce_rate,
          price: product.reference_unit_price,
          status: productStatus.approved,
          images: sortedImageData,
          co2Unit: product.material.co2_unit,
          costUnit: product.material.cost_unit,
          calculatedReduceRate: product.calculated_reduce_rate,
          calculatedEmissionIntensity: product.calculated_emission_intensity,
          calculatedMethod: product.calculated_method
        };
      }
    );
    setDisplayList(displayList);
  };

  // ページか計算方法を変更した時、お気に入りを取得
  useEffect((): void => {
    void (async () => {
      setLoading(true);
      await getList();
      try {
        await getList();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const loginErrorResponse = axiosError.response as response;
          if (setErrorText && setSnackbarOpen) {
            setErrorText(loginErrorResponse.data.message);
            setSnackbarOpen(true);
          }
        } else {
          if (setErrorText && setSnackbarOpen) {
            setErrorText(common);
            setSnackbarOpen(true);
          }
        }
      } finally {
        setLoading(false);
        setShowList(true);
      }
    })();
  }, [currentPage, selectedMethod]);

  // マイリスト追加
  const addMylistFun = async (productId: number, productName: string) => {
    try {
      const res = await addMylist(productId);
      setInfoText &&
        setInfoText(`マイリスト「${res.data.title}」に追加しました`);
      window.dataLayer.push({
        event: 'mylist',
        screen: window.location.pathname,
        category: 'button',
        action: 'favorite_mylist',
        label: productName,
        value: productId,
        userId,
        userRank,
        userType
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (!setErrorText || !setSnackbarOpen) return;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText(loginErrorResponse.data.message);
      } else {
        setErrorText(common);
      }
    } finally {
      await getList();
      setSnackbarOpen && setSnackbarOpen(true);
    }
  };

  // マイリスト削除
  const deleteMylistFun = async (productId: number, productName: string) => {
    try {
      const res = await deleteMylistProduct(productId);
      setInfoText &&
        setInfoText(`マイリスト「${res.data.title}」から削除しました`);

      window.dataLayer.push({
        event: 'mylist_del',
        screen: window.location.pathname,
        category: 'button',
        action: 'favorite_mylist_del',
        label: productName,
        value: productId,
        user_id: userId,
        user_type: userType,
        user_rank: userRank
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (!setErrorText || !setSnackbarOpen) return;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText(loginErrorResponse.data.message);
      } else {
        setErrorText(common);
      }
    } finally {
      await getList();
      setSnackbarOpen && setSnackbarOpen(true);
    }
  };

  // お気に入り削除
  const deleteFavoriteFun = async (productId: number, productName: string) => {
    try {
      await deleteFavorite(productId);
      setInfoText && setInfoText('お気に入りから削除しました');
      window.dataLayer.push({
        event: 'favorite_del',
        screen: window.location.pathname,
        category: 'button',
        action: 'favorite_favorite_del',
        label: productName,
        value: productId,
        user_id: userId,
        user_type: userType,
        user_rank: userRank
      });
      if (displayList.length === 1) {
        // ページに建材が1つある状態で削除した時、前のページにする
        setCurrentPage(currentPage - 1);
      } else {
        await getList();
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (!setErrorText || !setSnackbarOpen) return;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText(loginErrorResponse.data.message);
      } else {
        setErrorText(common);
      }
    } finally {
      setSnackbarOpen && setSnackbarOpen(true);
    }
  };

  const content = () => {
    let content;
    if (totalCount > 0) {
      // 表示建材あり
      content = (
        <Box sx={styles.materialList}>
          {displayList.map((item, index) => {
            return (
              <MaterialCard
                item={item}
                key={index}
                buttonSectionType="favorite"
                mylistReviewSection={true}
                statusSection={false}
                addMylistFun={addMylistFun}
                deleteMylistFun={deleteMylistFun}
                deleteFavoriteFun={deleteFavoriteFun}
              />
            );
          })}
          {totalPages > 1 ? (
            <CustomPagination
              currentPage={currentPage}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          ) : null}
        </Box>
      );
    } else {
      // お気に入り未追加
      content = (
        <>
          <Divider sx={styles.divider} />
          <Grid
            container
            direction="column"
            alignItems="center"
            sx={styles.content}
          >
            <Check sx={styles.icon} />
            <Typography
              sx={[
                styles.contentTitle,
                { '@media screen and (max-width: 960px)': { width: '216px' } }
              ]}
            >
              お気に入りの建材はまだありません
            </Typography>
            <Typography sx={styles.contentText}>
              建材検索で建材をお気に入り登録するとここに表示されます
            </Typography>
            <Button
              fullWidth
              onClick={() => navigate('/material')}
              sx={[styles.searchButton, primaryButtonOutlinedColor]}
              variant="outlined"
            >
              建材を検索する
            </Button>
          </Grid>
        </>
      );
    }

    return content;
  };

  return (
    <>
      <CustomHead title="お気に入り | CMCOS[コムコス]" />
      <CustomLoading loading={loading} />

      <Grid container ref={topRef} sx={styles.pageWrapper}>
        <SideBar />

        <Grid sx={styles.contentWrapper}>
          <Grid container sx={styles.contentHeader}>
            <Grid container sx={styles.headerLeft}>
              <Typography sx={styles.headerTitle}>お気に入り</Typography>
            </Grid>
            <Grid container sx={styles.headerRight}>
              <Box sx={{ width: '200px' }}>
                <CustomSelectField
                  list={productMethodList}
                  onChange={handleChangeMethod}
                  value={selectedMethod}
                  type="primary"
                />
              </Box>
            </Grid>
          </Grid>

          {showList && content()}
        </Grid>
      </Grid>

      <CustomSnackbar
        autoHideDuration={6000}
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        text={errorText}
        infoText={infoText}
      />
    </>
  );
};

export default FavoritesList;
